import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrder } from '../../store/slices/orderSlice';
import api from '../../services/api';
import {
  Box, Typography, Paper, Grid, Chip, Divider, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Stack, Avatar, Stepper, Step, StepLabel, Alert,
  FormControl, InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import {
  ArrowBack, LocalShipping, Receipt, Inventory2, Place, Payment, EventNote, QrCode2
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const PAYMENT_METHOD_LABELS = {
  bank_transfer: 'Bank Transfer',
  bank_card: 'Bank Card',
  paypal: 'PayPal',
  alipay: 'Alipay',
  wechat_pay: 'WeChat Pay'
};

const BANK_DETAILS = [
  { label: 'Bank Name', value: 'International Commerce Bank (ICB)' },
  { label: 'Account Name', value: 'OSESG Trading Platform LLC' },
  { label: 'Account No.', value: '8821-0044-9900-3712' },
  { label: 'SWIFT / BIC', value: 'ICBGUSD2XXX' },
  { label: 'IBAN', value: 'GB29ICBG60161331926819' }
];

const statusColors = {
  pending: 'warning', confirmed: 'info', processing: 'primary',
  manufacturing: 'primary', quality_check: 'info', ready_to_ship: 'info',
  in_transit_domestic: 'secondary', at_warehouse: 'secondary',
  shipped_international: 'secondary', customs_clearance: 'secondary',
  delivered: 'success', installed: 'success', completed: 'success',
  cancelled: 'error', disputed: 'error'
};

const formatMoney = (amount, currency = 'USD') => {
  if (amount === undefined || amount === null || amount === '') return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch (_) {
    return `${currency} ${Number(amount).toLocaleString()}`;
  }
};

const fieldRow = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
  </Grid>
);

const CustomerOrderDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { order, loading, error } = useSelector((s) => s.orders);

  const [payMethod, setPayMethod] = useState('');
  const [payReference, setPayReference] = useState('');
  const [companyQr, setCompanyQr] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState({ text: '', severity: 'info' });

  useEffect(() => { dispatch(fetchOrder(id)); }, [dispatch, id]);

  useEffect(() => {
    if (order) {
      setPayMethod(order.paymentMethod || 'bank_transfer');
      setPayReference(order.paymentDetails?.reference || '');
    }
  }, [order]);

  useEffect(() => {
    api.get('/payment-methods/company-qr').then(({ data }) => setCompanyQr(data.data)).catch(() => {});
  }, []);

  const savePaymentInfo = async () => {
    setSavingPayment(true);
    setPaymentMsg({ text: '', severity: 'info' });
    try {
      await api.put(`/orders/${order._id}/payment-info`, { paymentMethod: payMethod, reference: payReference });
      setPaymentMsg({ text: 'Payment info saved. Our team will confirm it shortly.', severity: 'success' });
      dispatch(fetchOrder(id));
    } catch (err) {
      setPaymentMsg({ text: err.response?.data?.error || 'Failed to save payment info.', severity: 'error' });
    }
    setSavingPayment(false);
  };

  if (loading || !order) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  const currency = order.pricing?.currency || 'USD';
  const addr = order.shippingAddress || {};
  const history = order.statusHistory || [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customer/orders')} variant="text">
          Back to orders
        </Button>
        <Typography variant="h5" fontWeight={700}>Order {order.orderNumber}</Typography>
        <Chip label={order.status} color={statusColors[order.status] || 'default'} />
        <Chip
          label={`Payment: ${order.paymentStatus || 'unpaid'}`}
          color={order.paymentStatus === 'paid' ? 'success' : order.paymentStatus === 'partial' ? 'info' : 'default'}
          variant="outlined"
        />
      </Stack>

      <Grid container spacing={2}>
        {/* Items */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              <Inventory2 sx={{ verticalAlign: 'middle', mr: 1 }} />
              Items ({order.items?.length || 0})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).map((item, idx) => {
                    const rawImg = item.product?.images?.[0];
                    const imgUrl = typeof rawImg === 'string' ? rawImg : rawImg?.url || null;
                    const lineTotal = item.totalPrice ?? (item.unitPrice * item.quantity);
                    return (
                      <TableRow key={item._id || idx}>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            {imgUrl ? (
                              <Avatar src={imgUrl} variant="rounded" sx={{ width: 48, height: 48 }} />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: '#eee' }}>
                                <Inventory2 fontSize="small" />
                              </Avatar>
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight={500}>{item.name || item.product?.name || 'Product'}</Typography>
                              {(item.osesgCode || item.product?.osesgCode) && (
                                <Typography variant="caption" color="text.secondary">Code: {item.osesgCode || item.product?.osesgCode}</Typography>
                              )}
                              {item.specifications && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {item.specifications}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatMoney(item.unitPrice, currency)}</TableCell>
                        <TableCell align="right"><strong>{formatMoney(lineTotal, currency)}</strong></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Status history */}
          {history.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                <EventNote sx={{ verticalAlign: 'middle', mr: 1 }} />
                Status Timeline
              </Typography>
              <Stepper activeStep={history.length - 1} alternativeLabel>
                {history.map((h, i) => (
                  <Step key={i} completed>
                    <StepLabel
                      optional={
                        <Typography variant="caption" color="text.secondary">
                          {h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}
                        </Typography>
                      }
                    >
                      {h.status}
                      {h.note && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {h.note}
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          )}
        </Grid>

        {/* Summary + addresses */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              <Receipt sx={{ verticalAlign: 'middle', mr: 1 }} />
              Order Summary
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">{formatMoney(order.pricing?.subtotal, currency)}</Typography>
              </Stack>
              {order.pricing?.shippingCost > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Shipping</Typography>
                  <Typography variant="body2">{formatMoney(order.pricing.shippingCost, currency)}</Typography>
                </Stack>
              )}
              {order.pricing?.customsCost > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Customs</Typography>
                  <Typography variant="body2">{formatMoney(order.pricing.customsCost, currency)}</Typography>
                </Stack>
              )}
              {order.pricing?.installationCost > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Installation</Typography>
                  <Typography variant="body2">{formatMoney(order.pricing.installationCost, currency)}</Typography>
                </Stack>
              )}
              {order.pricing?.tax > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Tax</Typography>
                  <Typography variant="body2">{formatMoney(order.pricing.tax, currency)}</Typography>
                </Stack>
              )}
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  {formatMoney(order.pricing?.total, currency)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              <Payment sx={{ verticalAlign: 'middle', mr: 1 }} />
              Payment
            </Typography>

            {(order.paymentStatus === 'partial' || order.paymentDetails?.amountPaid > 0) && (
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                  <Typography variant="body2" fontWeight={700}>{formatMoney(order.pricing?.total, currency)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Paid</Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    {formatMoney(order.paymentDetails?.amountPaid || 0, currency)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Remaining</Typography>
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    {formatMoney(order.remainingBalance ?? order.pricing?.total, currency)}
                  </Typography>
                </Grid>
              </Grid>
            )}

            {order.paymentStatus === 'paid' ? (
              <Grid container spacing={1}>
                {fieldRow('Method', PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod)}
                {fieldRow('Status', order.paymentStatus)}
                {fieldRow('Reference', order.paymentDetails?.reference)}
                {order.paymentDetails?.confirmedAt && fieldRow('Confirmed', new Date(order.paymentDetails.confirmedAt).toLocaleString())}
              </Grid>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {order.paymentStatus === 'partial'
                    ? `A deposit has been received. ${formatMoney(order.remainingBalance ?? order.pricing?.total, currency)} still due — choose how you'd like to pay the rest.`
                    : "This order is unpaid. Choose how you'd like to pay below."}
                </Alert>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select label="Payment Method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                    {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(payMethod === 'alipay' || payMethod === 'wechat_pay') && (() => {
                  const isAlipay = payMethod === 'alipay';
                  const qrUrl = isAlipay ? companyQr?.alipayQrUrl : companyQr?.wechatQrUrl;
                  const accountName = isAlipay ? companyQr?.alipayAccountName : companyQr?.wechatAccountName;
                  return (
                    <Box sx={{ textAlign: 'center', py: 2, mb: 2, borderRadius: 2, bgcolor: isAlipay ? '#00A1E915' : '#07C16015' }}>
                      {qrUrl ? (
                        <>
                          <Box component="img" src={`${ASSET_BASE_URL}${qrUrl}`} alt={`${isAlipay ? 'Alipay' : 'WeChat Pay'} QR`}
                            sx={{ maxHeight: 200, maxWidth: '100%', borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: 'white', p: 1 }} />
                          {accountName && <Typography variant="body2" color="text.secondary" mt={1}>{accountName}</Typography>}
                          <Typography variant="caption" color="text.secondary" display="block">
                            Scan with your {isAlipay ? 'Alipay' : 'WeChat'} app to pay {formatMoney(order.remainingBalance ?? order.pricing?.total, currency)}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <QrCode2 sx={{ fontSize: 72, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary" display="block">
                            QR code not configured yet — please choose another payment method.
                          </Typography>
                        </>
                      )}
                    </Box>
                  );
                })()}

                {payMethod === 'bank_transfer' && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8f9fa', mb: 2 }}>
                    {BANK_DETAILS.map(({ label, value }) => (
                      <Box key={label} display="flex" justifyContent="space-between" py={0.4}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="caption" fontWeight={600}>{value}</Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                <TextField
                  fullWidth size="small" sx={{ mb: 2 }}
                  label={payMethod === 'alipay' || payMethod === 'wechat_pay' ? 'Transaction ID (after paying)' : 'Payment Reference'}
                  value={payReference} onChange={(e) => setPayReference(e.target.value)}
                  helperText="Enter this after completing payment so our team can confirm it faster"
                />

                <Button fullWidth variant="contained" disabled={savingPayment} onClick={savePaymentInfo}>
                  {savingPayment ? <CircularProgress size={18} /> : 'Save Payment Info'}
                </Button>

                {paymentMsg.text && <Alert severity={paymentMsg.severity} sx={{ mt: 1.5 }}>{paymentMsg.text}</Alert>}
              </>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              <Place sx={{ verticalAlign: 'middle', mr: 1 }} />
              Shipping Address
            </Typography>
            <Typography variant="body2">{addr.contactName}</Typography>
            <Typography variant="body2">{addr.phone}</Typography>
            <Typography variant="body2" color="text.secondary">
              {[addr.address, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean).join(', ')}
            </Typography>
          </Paper>

          {order.shipment?.shipmentNumber && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                <LocalShipping sx={{ verticalAlign: 'middle', mr: 1 }} />
                Shipment
              </Typography>
              <Typography variant="body2" gutterBottom>
                Tracking #: <strong>{order.shipment.shipmentNumber}</strong>
              </Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<LocalShipping />}
                component={Link}
                to={`/track/${order.shipment.shipmentNumber}`}
              >
                Track Shipment
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerOrderDetail;
